import { Affix, Button, Card, Descriptions } from "antd";
import ListTable from "common/ListTable";
import { DISCARDED_DATA_LOG_DETAILS } from "apiServices/API_ENDPOINTS";
import { getData } from "apiServices/common";
import { alertPop } from "apiServices/common/helper";
import Header from "components/Common/Header";
import { Fragment, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

export default function FailedItemsView() {
  const navigate = useNavigate();
  const { sync_date, module_name } = useParams();

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedRecord, setSelectedRecord] = useState(null);

  const fetchData = useCallback(
    async (page = 1, pageSize = 10) => {
      setLoading(true);
      setError(null);
      let response = await getData(`${DISCARDED_DATA_LOG_DETAILS}`, false, {
        sync_date: sync_date,
        module_name: module_name,
        page: page,
        per_page: pageSize,
      });
      if (response && response?.status === 200) {
        let masterData = response?.data?.data?.data;
        setData(masterData);
        setPagination({
          current: page,
          pageSize: pageSize,
          total: response?.data?.total || 0,
        });
      } else {
        alertPop(
          "error",
          response?.messages ? response?.messages : "Something Went Wrong!",
        );
        setError("Could not load this list.");
      }
      setLoading(false);
    },
    [sync_date, module_name],
  );

  useEffect(() => {
    fetchData(pagination.current, pagination.pageSize);
  }, [sync_date, module_name, pagination.current, pagination.pageSize, fetchData]);

  const handleTableChange = (pagination) => {
    setPagination({
      ...pagination,
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  return (
    <>
      <Affix offsetTop={0}>
        <Card className="p-fixed">
          <Header
            title="View Failed Items"
            actions={[
              <Affix offsetTop={20}>
                <Button
                  size="small"
                  key={1}
                  danger
                  onClick={() => navigate(-1)}
                >
                  Discard
                </Button>
              </Affix>,
            ]}
          ></Header>
        </Card>
      </Affix>
      <Card>
        <ListTable
          style={{ marginTop: "20px" }}
          error={error}
          onRetry={() => fetchData(pagination.current, pagination.pageSize)}
          emptyText="No records to show yet."
          dataSource={data}
          columns={[
            {
              title: "Sl No",
              dataIndex: "index",
              key: "index",
              width: 100,
              render: (text, r, i) => <span>{i + 1}</span>,
            },
            {
              title: "User ID",
              dataIndex: "user_id",
              key: "user_id",
            },
            {
              title: "Sync Date",
              dataIndex: "sync_date",
              key: "sync_date",
            },
            {
              title: "Sync Type",
              dataIndex: "sync_type",
              key: "sync_type",
            },
            {
              title: "Module Name",
              dataIndex: "module_name",
              key: "module_name",
            },
            {
              title: "Action",
              render: (text, record) => (
                <Button type="link" onClick={() => setSelectedRecord(record)}>
                  View Details
                </Button>
              ),
            },
          ]}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
          }}
          loading={loading}
          onChange={handleTableChange}
        />
      </Card>
      <Card title="Details" style={{ marginTop: "20px" }}>
        {selectedRecord ? (
          <Descriptions bordered>
            <Descriptions.Item
              label={<span style={{ fontWeight: "bold" }}>Sync Type:</span>}
            >
              {selectedRecord?.sync_type}
            </Descriptions.Item>
            <Descriptions.Item
              label={<span style={{ fontWeight: "bold" }}>Sync Date:</span>}
            >
              {selectedRecord?.sync_date}
            </Descriptions.Item>
            <Descriptions.Item
              label={<span style={{ fontWeight: "bold" }}>Module Name:</span>}
            >
              {selectedRecord?.module_name}
            </Descriptions.Item>

            <Descriptions.Item
              label={<span style={{ fontWeight: "bold" }}>Details:</span>}
              span={3}
            >
              <Descriptions bordered>
                {selectedRecord?.details &&
                Object.keys(selectedRecord.details).length > 0
                  ? Object.entries(selectedRecord.details).map(
                      ([key, value]) => {
                        if (key === "extended_details" && value?.length > 0) {
                          return (
                            <Descriptions.Item
                              key={key}
                              label={
                                <span
                                  style={{
                                    fontWeight: "bold",
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {key.split("_").join(" ")}
                                </span>
                              }
                            >
                              <Descriptions bordered>
                                {value?.length > 0
                                  ? value.map((val) => (
                                      <Fragment key={val?.key}>
                                        {Object.entries(val).map(([k, v]) => (
                                          <Descriptions.Item
                                            key={k}
                                            label={
                                              <span
                                                style={{
                                                  fontWeight: "bold",
                                                  textTransform: "capitalize",
                                                }}
                                              >
                                                {k.split("_").join(" ")}
                                              </span>
                                            }
                                          >
                                            {v}
                                          </Descriptions.Item>
                                        ))}
                                      </Fragment>
                                    ))
                                  : "Extended details not found"}
                              </Descriptions>
                            </Descriptions.Item>
                          );
                        }

                        let span = 1;
                        if (key === "Quantity") {
                          span = 2;
                        }
                        if (
                          module_name === "Hourly Production Record" &&
                          key === "Buyer"
                        ) {
                          span = 3;
                        }
                        return (
                          <Descriptions.Item
                            key={key}
                            label={
                              <span
                                style={{
                                  fontWeight: "bold",
                                  textTransform: "capitalize",
                                }}
                              >
                                {key.split("_").join(" ")}
                              </span>
                            }
                            span={span}
                          >
                            {value}
                          </Descriptions.Item>
                        );
                      },
                    )
                  : "No details found"}
              </Descriptions>
            </Descriptions.Item>
            <Descriptions.Item
              label={<span style={{ fontWeight: "bold" }}>Error</span>}
              span={3}
            >
              {selectedRecord?.errors?.length > 0
                ? selectedRecord?.errors.map((item, index) => (
                    <Fragment key={index}>
                      {item}
                      <br />
                    </Fragment>
                  ))
                : "No errors found"}
            </Descriptions.Item>
          </Descriptions>
        ) : (
          "Click on a record to view details"
        )}
      </Card>
    </>
  );
}
